import React, {
  createContext,
  useContext,
  useEffect,
  useState
} from 'react';
import { Button } from 'antd';

import ModalTask from 'components/Organisms/Modals/ModalTask/ModalTask';
import leafPng from 'assets/images/leaf.png';
import Step2 from './Step2/Step2';
import Step1 from './Step1/Step1';
import { Entity, Loan, Task, TaskResponse, TaskState } from 'gql/graphql';
import { useGetTaskResponsesByTask } from 'services/task/querys';
import { useRemoveTaskResponse } from 'services/task/mutations';
import Step3 from './Step3/Step3';
import { CreateTaskResponseContext, LoanContext, OwnerSelectContext } from '../../Dashboard';
import { useGetEntitiesByOwner } from '../../../../../services/entity/querys';
import DropdownEntities from './DropdownEntities/DropdownEntities';
import DropdownBlue from 'components/Atoms/Dropdown/DropdownBlue/DropdownBlue';
import StateTag from 'components/Atoms/Tags/StateTag/StateTag';
import { ModuleKeys } from 'services/task/modules-keys.constanst';
import { findTaskRules } from '../../RulesTaskCards.constanst';
import { TaskOpenModalContext } from '../../../Private';
import { Tooltip } from 'react-tooltip';
import { canEditTaskInfo } from '@common/utils/functions/mains';
import { useModalContext } from '../../../../../sentry/contexts/useModalContext';

const STEPS = 3;

export interface SubmitContextInterface {
  beforeStep?: number;
  step?: number;
  active?: boolean;
  metadata?: any;
}

export const TaskContext = createContext<Task | {}>({});
export const TaskResponsesContext = createContext<TaskResponse | {}>({});
export const SubmitContext = createContext<SubmitContextInterface | {}>({});

interface EntityGuarantorCreationProps {
  task: Task;
}

const EntityGuarantorCreation = ({
  task
}: EntityGuarantorCreationProps) => {
  const loan: Loan | any = useContext(LoanContext);
  const { value: openContext, setHandler }: any = useContext(TaskOpenModalContext);
  const { value: isCreateResponseTask }: any = useContext(CreateTaskResponseContext);
  const { value: ownerSelect, setValue: setOwnerSelect }: any = useContext(OwnerSelectContext);

  const { data: taskResponses, reexecute: getTaskResponses } =
    useGetTaskResponsesByTask({
      variables: { findResponsesByTask: { taskId: task._id } }
    });
    
  const { data: entities, reexecute: getEntitiesByOwner } = useGetEntitiesByOwner(
    {
      variables: { ownerId: ownerSelect },
      pause: true
    }
  );
  
  const { data: taskResponsesEntityDocs } = useGetTaskResponsesByTask({
    variables: {
      findResponsesByTask: { loanId: loan._id, taskKey: ModuleKeys.ENTITY_DOCS }
    }
  });

  const { execute: removeTaskResponse } = useRemoveTaskResponse();

  const [isModalTaskVisible, setModalTaskVisible] = useState(false);
  const [isReview, setIsReview] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const isReadOnly = !canEditTaskInfo(task);
  
  const [step, setStep] = useState(isReadOnly ? 3 : 1);
  const [responseStep1, setResponseStep1] = useState<any>();
  const [responseStep1Draft, setResponseStep1Draft] = useState<boolean>(false);
  const [responsesStep2, setResponsesStep2] = useState<any[]>();
  const [step2Cache, setStep2Cache] = useState<any[]>();
  const [step2ConfirmPerson, setStep2ConfirmPerson] = useState<any>();
  const [responseStep3, setResponseStep3] = useState<any>();
  const [entity, setEntity] = useState<Entity | any>();
  const [handlerSubmit, setHandlerSubmit] = useState<SubmitContextInterface>(
    {}
  );

  useEffect(() => {
    if (step === 1 && !isReadOnly) setResponsesStep2(undefined);
  }, [step, isReadOnly])

  useEffect(() => {
    if (openContext === 'entity_guarantor' || openContext === ModuleKeys.ENTITY_GUARANTORS) {
      setModalTaskVisible(true);
      setHandler();
    } else if (!!openContext)
      setModalTaskVisible(false);
  }, [openContext]);

  useModalContext({ isModalVisible: isModalTaskVisible, modalKey: 'entity_guarantor', task, step });

  useEffect(() => {
    if (entities) {
      const _entity = entities.find((item) =>
        item.loans.find((item2) => item2._id === loan._id)
      );
      setEntity(_entity);
    }
  }, [entities]);

  useEffect(() => {
    if (ownerSelect || isCreateResponseTask === 'success') getEntitiesByOwner();
  }, [ownerSelect, isCreateResponseTask]);

  // SI SE CAMBIA ESTA LOGICA SE DEBE CAMBIAR EN useDashboard.tsx linea 239
  useEffect(() => {
    const step3 = taskResponses?.find((item) => item.step === 3);
    setResponseStep3(step3);

    // Si ya se encuentra en el paso 3, refrescar las entidades
    if(step3)  
      getEntitiesByOwner({ requestPolicy: 'network-only' });

    const step1 = taskResponses?.find((item) => item.step === 1);
    let ownerId = step1?.metadata?.ownerId;
    
    setOwnerSelect(step1?.metadata?.ownerId);
    setResponseStep1(step1);

    if (taskResponses && taskResponses.length > 0) {
      const step2 = taskResponses?.filter(
        (item) =>
          item.step === 2 &&
          item.state !== TaskState.Cache &&
          item.key !== 'confirm-persons'
      );
      const step2Cache = taskResponses?.filter(
        (item) =>
          item.step === 2 &&
          item.state === TaskState.Cache &&
          item.key !== 'confirm-persons'
      );
      const confirmPerson = taskResponses?.find(
        (item) => item.key === 'confirm-persons'
      );
      
      if (!step1?.metadata?.ownerId){
        ownerId = step2[0] && step2[0]?.metadata?.ownerId;
        
        setOwnerSelect(ownerId);
      }
      
      //El owner que se escogio al inicio es posible borrarlo
      //En este caso se debe validar el siguiente owner registrado 
      if(!step2.find(item => item.metadata.ownerId === ownerId)){
        const newOwner = step2.find(item => item.metadata.ownerId !== ownerId)
        setOwnerSelect(newOwner?.metadata.ownerId || newOwner?.metadata._id);
      }

      setStep2ConfirmPerson(confirmPerson);
      setResponsesStep2(step2);
      setStep2Cache(step2Cache);

      if (step2) {
        const isReview = step2.find((item) => item.state === TaskState.Review);
        setIsReview(!!isReview);

        const _isApproved = step2.every((item) => item.state === TaskState.Approved);
        setIsApproved(!!_isApproved);
      }

      if (!step2 && !step3 && step1 && !isReadOnly) setStep(1);
      else if (step2 && step2?.length > 0 && !step3 && !isReadOnly) setStep(2);
      else if (step3 && !isReadOnly) setStep(3);
    }
  }, [taskResponses, isReadOnly]);

  const actionOwnership = (action: 'unconfirmed' | 'confirmed') => {
    if (action === 'confirmed') {
      setHandlerSubmit({ step: 2, active: true });
    } else {
      if (step2ConfirmPerson) {
        removeTaskResponse({ taskResponseId: step2ConfirmPerson._id });
      }
      setStep(2);
    }
  };

  const isConfirmedEntityDocs = () =>
    taskResponsesEntityDocs?.some((item) => item.state === TaskState.Approved);

  if (!task) return null;

  const { taskName } = findTaskRules(ModuleKeys.ENTITY_GUARANTORS);

  const isOptionEntityExist = entities && entities?.length > 0 && responseStep1?.label === 'Existing entity';

  const renderGuarantors = (responses: any, maxVisible = 3) => {
    const filteredResponses = responses.filter((response: any) => response.metadata.ownership >= 20);
    const visibleGuarantors = filteredResponses.slice(0, maxVisible);
    const remainingCount = filteredResponses.length - maxVisible;

    return (
      <>
        {visibleGuarantors.map((response: any, index: number) => (
          <React.Fragment key={response._id}>
            <span className="bold">
              {response.metadata.name} {response.metadata.lastname} ({response.metadata.ownership}%)
            </span>
            {index < visibleGuarantors.length - 1 && (
              remainingCount === 0 && index === visibleGuarantors.length - 2
                ? ' and '
                : ', '
            )}
          </React.Fragment>
        ))}
        {remainingCount > 0 && (
          <>
            , and{' '}
            <span
              className="bold"
              data-tooltip-id="remaining-guarantors-tooltip"
              data-tooltip-html={filteredResponses
                .slice(maxVisible)
                .map(
                  (remaining: any) =>
                    `${remaining.metadata.name} ${remaining.metadata.lastname}`
                )
                .join('<br/>')}
              data-tooltip-class-name="remaining-guarantors-tooltip"
              style={{ textDecoration: 'underline' }}
            >
              {remainingCount} more
            </span>
            <Tooltip id="remaining-guarantors-tooltip" />
          </>
        )}
      </>
    );
  };
  
  const renderTracker = () => (
    <div style={{ height: '100%' }}>
      <span className='title-summary'>Task summary</span>
      <div className='mt-20'>
        <p className="placeholder">
          {!responseStep3 && (
            <>
              {'My entity is '}
              <DropdownEntities />
            </>

          )}
          {isOptionEntityExist && (
            <>
              {responseStep3 ? (
                <>
                  {'My entity is '}
                  <span className="bold">
                    {responseStep1?.metadata?.entity}
                  </span>
                  <p className="placeholder" style={{ paddingTop: 8 }}>
                    The guarantors are{' '}
                    {renderGuarantors(responsesStep2)}
                  </p>
                </>
              ) : (
                <>
                  {' Its ownership is '}
                  <DropdownBlue
                    label="unconfirmed"
                    items={[
                      { key: 'confirmed', label: 'Confirmed' },
                      { key: 'unconfirmed', label: 'Unconfirmed' }
                    ]}
                    onClickSelect={(key: string) => actionOwnership(key as 'confirmed' | 'unconfirmed')}
                    className="dropdown-blue-bold link-blue normal"
                  />
                  {` and its 4 documents are `}
                  <span className="bold" style={{ padding: 0 }}>
                    {isConfirmedEntityDocs() ? 'confirmed' : 'unconfirmed'}
                  </span>
                </>
              )}
            </>
          )}
        </p>
        {!isOptionEntityExist && ((responsesStep2 && responsesStep2?.length > 0) || responseStep3) && (
          <div className="owners-tags">

            <div className="placeholder">
              {responseStep3 &&
                <span className='description'>
                  {'My entity is '}
                  <span className="bold">
                    {responseStep1?.metadata?.entity || 'not formed yet'}
                  </span>
                </span>
              }
              <p className="placeholder" style={{ paddingTop: 8, marginTop: 40 }}>
                The guarantors are{' '}
                {renderGuarantors(responsesStep2)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const canNavigateToStep = (targetStep: number): boolean => {
    if (targetStep === 1) return true;
    if (targetStep === 2) return !!responseStep1;

    // Puede ir al step 3 si hay responsesStep2 con datos
    if (targetStep === 3) return !!(responsesStep2 && responsesStep2.length > 0);
    return false;
  };

  return (
    <section className="EntityGuarantorCreation">
      <TaskContext.Provider value={task}>
        <ModalTask
          task={task}
          visible={isModalTaskVisible}
          handlerSetVisibile={setModalTaskVisible}
          disabledDots={isReadOnly ? false : !!responseStep3}
          canNavigateToStep={canNavigateToStep}
          tracker={renderTracker()}
          isConfirmModalClose={!isApproved && !isReview}
          responses={taskResponses}
          renderButtonTrigger={(openModal: any) => (
            <Button
              className={`button-open-modal button-open-modal-${task.state}`}
              onClick={() => openModal(true)}
            >
              {(task.state === TaskState.Review || task.state === TaskState.Approved) ? 'View task' :
                (task.state === TaskState.Incomplete) ? 'Complete task' :
                (task.state === TaskState.RequestError) ? 'Resolve issues' :
                  'Begin Task'
              }
            </Button>
          )}
          header={{
            title: <span>{taskName.toUpperCase()}</span>,
            actionLeft:
              task.state === TaskState.Review ? <StateTag state={TaskState.Review} size="large" /> : 
              task.state === TaskState.Approved ? <StateTag state={TaskState.Approved} isLabelLocked size="large" /> :
              <img src={leafPng} alt="icon" className="paperPng icon-task-state" />
          }}
          animationDirection="onRight"
          stepsTotal={STEPS}
          currentStep={step}
          setStep={setStep}
        >
          <div className="children">
            {taskResponses && (
              <TaskResponsesContext.Provider value={taskResponses}>
                <SubmitContext.Provider value={handlerSubmit}>

                  {step === 1 && (
                    <Step1
                      isReadOnly={isReadOnly}
                      setStep={setStep}
                      responsesStep2={responsesStep2}
                      setResponseStep1Draft={setResponseStep1Draft}
                      step2Confirm={step2ConfirmPerson}
                      getTaskResponses={getTaskResponses}
                      setHandlerSubmit={setHandlerSubmit}
                      closeTask={() => setModalTaskVisible(false)}
                    />
                  )}
                  {step === 2 && (
                    <Step2
                      setStep={setStep}
                      responseStep1={responseStep1}
                      responsesStep2={responsesStep2}
                      step2Cache={step2Cache}
                      step2Confirm={step2ConfirmPerson}
                      setHandlerSubmit={setHandlerSubmit}
                      closeTask={() => setModalTaskVisible(false)}
                    />
                  )}
                  {step === 3 && responsesStep2 && (
                    <Step3
                      setStep={setStep}
                      responseStep1={responseStep1}
                      responsesStep2={responsesStep2}
                      closeTask={() => setModalTaskVisible(false)}
                      entity={entity}
                    />
                  )}
                </SubmitContext.Provider>
              </TaskResponsesContext.Provider>
            )}
          </div>
        </ModalTask>
      </TaskContext.Provider>
    </section>

  );
};
export default EntityGuarantorCreation;
